import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import { numberSetService, type CustomerNumberSet } from '../../services/numberSetService'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import './ModernLobbyHuayForms.css'

/** หน้า เลขชุด — ดึงรายการเลขชุดทั้งหมดมาแสดง (ตรงกับ user-classic NumberSetPage, UI แบบ user-modern) */
const ModernLobbyHuaySetPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const [numberSets, setNumberSets] = useState<CustomerNumberSet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadList = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const list = await numberSetService.list()
      setNumberSets(list)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'โหลดรายการไม่สำเร็จ'
      const friendly =
        msg === 'Not Found' || msg.toLowerCase().includes('not found')
          ? 'ยังไม่มีเลขชุด หรือไม่สามารถโหลดรายการได้ในขณะนี้ กรุณากด "จับวินเลข" เพื่อสร้างเลขชุด'
          : msg
      setError(friendly)
      setNumberSets([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  const handleEditNumberSet = (id: string) => {
    navigate(`/lobby-huay/set/edit/${id}`)
  }

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('th-TH', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="htd-form">
      <div className="htd-lh-actions">
        <Link to="/lobby-huay/set/create" className="htd-lh-action htd-lh-action--primary">
          <i className="fas fa-plus-circle" aria-hidden />
          สร้างเลขชุด
        </Link>
        <Link to="/lobby-huay/win-number" className="htd-lh-action htd-lh-action--secondary">
          <i className="fas fa-random" aria-hidden />
          จับวินเลข
        </Link>
      </div>
      <div className="row mx__1 mt-1" style={{ rowGap: '0.55rem' }}>
        {error && (
          <div className="col-12 px-1">
            <div className="htd-lh-alert" role="alert">
              <span>{error}</span>
            </div>
          </div>
        )}
        {loading ? (
          <div className="col-12 px-1">
            <div className="htd-slip__state" aria-busy="true">
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.4rem', color: '#7dd3fc' }} />
              <p>กำลังโหลด...</p>
            </div>
          </div>
        ) : numberSets.length === 0 ? (
          <div className="col-12 px-1">
            <div className="htd-slip__state">
              <p>ยังไม่มีเลขชุด กรุณากดปุ่ม &quot;จับวินเลข&quot; หรือ &quot;สร้างเลขชุด&quot; ด้านบนเพื่อเพิ่มเลขชุดใหม่</p>
            </div>
          </div>
        ) : (
          numberSets.map((numberSet, i) => (
            <div key={numberSet._id} className="col-12 px-1">
              <button
                type="button"
                className="htd-lh-set-card"
                style={{ animationDelay: `${0.05 + i * 0.04}s` }}
                onClick={() => handleEditNumberSet(numberSet._id)}
              >
                <h5 className="htd-lh-set-card__name">{numberSet.name}</h5>
                <span className="htd-lh-set-card__date">
                  {formatDate(numberSet.updatedAt || numberSet.createdAt)}
                </span>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default ModernLobbyHuaySetPage
